import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button } from '../atoms';
import { useToast } from './Toast';

/**
 * ViewAsMenu — the super_admin "View as" control for the top app bar.
 *
 * Renders a "View as" button that opens a dropdown of org users grouped by role.
 * Selecting a user POSTs /api/switch-user/:id and hard-reloads so the app
 * re-renders as that user (staff workqueue, an agent's book-scoped view, or the
 * client portal — whichever the target's role maps to).
 *
 * Visibility is the caller's responsibility (mount only for super_admin/admin).
 * The switch itself is super_admin-only server-side; an admin who reaches it gets
 * a clear toast rather than a silent no-op.
 *
 * Lane build/switch-user.
 */

interface CandidateUser {
  id: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  employee_title: string | null;
}

/** The current user's id — so we can mark / skip "you" in the list. */
export interface ViewAsMenuProps {
  currentUserId?: string;
}

// Role display order + labels for the grouped picker.
const ROLE_ORDER = ['super_admin', 'admin', 'administrator', 'agent', 'analyst', 'consultant', 'client'];
const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admins',
  admin: 'Admins',
  administrator: 'Office Administrators',
  agent: 'Agents',
  analyst: 'Analysts',
  consultant: 'Consultants',
  client: 'Clients',
};

const nameOf = (u: CandidateUser) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;

export function ViewAsMenu({ currentUserId }: ViewAsMenuProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch candidates only once the menu is first opened.
  const candidatesQuery = useQuery<CandidateUser[]>({
    queryKey: ['switch-user-candidates'],
    queryFn: ({ signal }) => api.get<CandidateUser[]>('/api/users-list', signal),
    enabled: open,
    staleTime: 60_000,
  });

  // Position the (body-portaled) dropdown under the button + close on outside
  // click / Escape. The menu is portaled to <body> so it escapes the sticky
  // header's overflow:hidden + z-index stacking context, which otherwise clips it
  // and paints it behind the page content. Reposition on scroll/resize.
  useEffect(() => {
    if (!open) { setMenuPos(null); return; }
    const place = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    };
    place();
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const grouped = useMemo(() => {
    const users = candidatesQuery.data ?? [];
    const byRole = new Map<string, CandidateUser[]>();
    for (const u of users) {
      const arr = byRole.get(u.role) ?? [];
      arr.push(u);
      byRole.set(u.role, arr);
    }
    const knownFirst = ROLE_ORDER.filter((r) => byRole.has(r));
    const others = [...byRole.keys()].filter((r) => !ROLE_ORDER.includes(r)).sort();
    return [...knownFirst, ...others].map((role) => ({
      role,
      label: ROLE_LABELS[role] ?? role,
      users: (byRole.get(role) ?? []).sort((a, b) => nameOf(a).localeCompare(nameOf(b))),
    }));
  }, [candidatesQuery.data]);

  async function switchTo(u: CandidateUser) {
    setSwitchingId(u.id);
    try {
      await api.post(`/api/switch-user/${u.id}`);
      // Hard reload so the SPA re-renders under the new session's role.
      window.location.assign('/');
    } catch (err) {
      setSwitchingId(null);
      const msg =
        err instanceof ApiClientError && err.status === 403
          ? 'Only a super admin can switch users.'
          : `Could not switch to ${nameOf(u)}.`;
      toast({ variant: 'error', message: msg });
    }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        iconLeft={
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        }
      >
        View as
      </Button>

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: menuPos.top,
            right: menuPos.right,
            zIndex: 1000,
            width: 280,
            maxHeight: Math.min(420, window.innerHeight - menuPos.top - 16),
            overflowY: 'auto',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-lg)',
            boxShadow: '0 8px 24px rgba(15,23,24,0.16)',
            padding: 'var(--sp-2)',
          }}
        >
          {candidatesQuery.isLoading && (
            <div style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)' }}>
              Loading users…
            </div>
          )}
          {candidatesQuery.isError && (
            <div style={{ padding: 'var(--sp-3)', color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)' }}>
              Could not load users.
            </div>
          )}
          {!candidatesQuery.isLoading && !candidatesQuery.isError && grouped.length === 0 && (
            <div style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)' }}>
              No users to switch to.
            </div>
          )}

          {grouped.map((group) => (
            <div key={group.role} style={{ marginBottom: 'var(--sp-2)' }}>
              <div
                style={{
                  padding: '4px var(--sp-2)',
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 'var(--fw-semibold)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--ls-wide, 0.04em)',
                  color: 'var(--color-ink-2)',
                }}
              >
                {group.label}
              </div>
              {group.users.map((u) => {
                const isSelf = u.id === currentUserId;
                const busy = switchingId === u.id;
                return (
                  <button
                    key={u.id}
                    type="button"
                    role="menuitem"
                    disabled={isSelf || busy || switchingId !== null}
                    onClick={() => switchTo(u)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1,
                      width: '100%',
                      textAlign: 'left',
                      padding: '6px var(--sp-2)',
                      border: 'none',
                      borderRadius: 'var(--r-md)',
                      background: 'transparent',
                      color: 'var(--color-ink)',
                      fontFamily: 'inherit',
                      fontSize: 'var(--fs-sm)',
                      cursor: isSelf || switchingId !== null ? 'default' : 'pointer',
                      opacity: isSelf ? 0.5 : busy ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelf && switchingId === null) e.currentTarget.style.background = 'var(--color-bg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span style={{ fontWeight: 'var(--fw-medium)' }}>
                      {nameOf(u)}
                      {isSelf && <span style={{ color: 'var(--color-ink-2)', fontWeight: 'var(--fw-regular, 400)' }}> (you)</span>}
                      {busy && <span style={{ color: 'var(--color-ink-2)' }}> · switching…</span>}
                    </span>
                    <span style={{ color: 'var(--color-ink-2)', fontSize: 'var(--fs-xs)' }}>{u.email}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
